import { ColDef } from "ag-grid-community";
// From `types` rather than the package index: the index also re-exports the API client, which
// would make this pure helper drag in axios.
import { CURRENCY_EUR } from "../api/currency/types";
import { currencyCode, currencySymbol } from "./formatters";

/**
 * Builds the grid column(s) for a money figure that may exist in **two currencies at once**.
 *
 * ## Why a column pair rather than one formatted cell
 *
 * These used to render as `45000.00L · 320.00€` in a single cell. Two independent balances squeezed
 * into one column read as one amount with a footnote — LEK looked like *the* figure and EUR like a
 * remark about it. They are neither halves of a total nor conversions of each other: a settlement
 * is partitioned by the currency each of its orders was registered in and paid out as two separate
 * transactions. Giving each its own column states that, and makes both sortable and filterable on
 * their own, which one merged string never was.
 *
 * ## The gate lives in here
 *
 * Off PostmanAL this returns a **single** column with the original header and formatting, so those
 * deployments render exactly what they rendered before. Callers therefore never write the feature
 * check themselves — the same reasoning that put the gate inside `DualMoney` rather than in its
 * twenty callers. Spread the result into the column list:
 *
 * ```ts
 * ...dualMoneyColumns({
 *     field: "sumValue",
 *     headerName: strings?.value,
 *     dualCurrencyEnabled,
 *     defaultCurrencyId,
 *     width: 150,
 * }),
 * ```
 */
export type DualMoneyColumnsOptions = Omit<ColDef, "field" | "headerName"> & {
	/** Row field holding the local-currency amount. */
	field: string;
	/** Row field holding the EUR amount. Defaults to `<field>Eur`. */
	eurField?: string;
	/** Base header, e.g. `"Shuma"`. The currency code is appended when the pair is rendered. */
	headerName?: string;
	/** From `useCurrencies()`. False collapses the pair back to a single column. */
	dualCurrencyEnabled: boolean;
	/** From `useCurrencies()`. Names the local half — `"Shuma LEK"` on PostmanAL. */
	defaultCurrencyId?: number | null;
};

/**
 * `-` for "no value at all", em dash for "this settlement had no side in this currency".
 *
 * The distinction matters: `0.00 €` on a LEK-only settlement invites someone to ask which EUR
 * orders made up that zero, and there were none.
 */
const NO_AMOUNT = "—";

/**
 * One amount, exactly as `dualMoneyColumns` renders it — `45,000.00 L`, or `—` when this
 * settlement had no side in this currency.
 *
 * Exported because an **export has to say what the screen says**. The settlements and refusals
 * sheets build their rows from the API data rather than from the grid, so they format through
 * this rather than through a second implementation that would drift from it — and a bare `45000`
 * in a spreadsheet cannot be told apart from a EUR one.
 */
export const formatDualMoneyAmount = (
	value: any,
	currencyId?: number | null
): string => {
	if (
		value === null ||
		value === undefined ||
		value?.toString().trim() === ""
	) {
		return NO_AMOUNT;
	}
	const amount = Number(value);
	if (!Number.isFinite(amount)) return NO_AMOUNT;
	if (amount === 0) return NO_AMOUNT;

	return `${new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	}).format(amount)} ${currencySymbol(currencyId)}`;
};

const amountFormatter =
	(currencyId?: number | null) =>
	(params: any): string =>
		formatDualMoneyAmount(params?.value, currencyId);

export const dualMoneyColumns = ({
	field,
	eurField = `${field}Eur`,
	headerName,
	dualCurrencyEnabled,
	defaultCurrencyId,
	...colDef
}: DualMoneyColumnsOptions): ColDef[] => {
	const localColumn: ColDef = {
		...colDef,
		field,
		headerName: dualCurrencyEnabled
			? `${headerName ?? ""} ${currencyCode(defaultCurrencyId)}`.trim()
			: headerName,
		// Off PostmanAL the row carries no currency id at all, so this resolves to the deployment
		// symbol via currencySymbol's fallback — identical to what the single column printed.
		valueFormatter: amountFormatter(
			dualCurrencyEnabled ? defaultCurrencyId : undefined
		),
	};

	if (!dualCurrencyEnabled) return [localColumn];

	return [
		localColumn,
		{
			...colDef,
			field: eurField,
			headerName: `${headerName ?? ""} ${currencyCode(
				CURRENCY_EUR
			)}`.trim(),
			valueFormatter: amountFormatter(CURRENCY_EUR),
		},
	];
};

export default dualMoneyColumns;
